"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { HolesLayer } from "./overlayKit/HolesLayer";
import { DevaKathaHypeCta, ctaBeatsFromWords } from "./DevaKathaHypeCta";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Ukhimath Omkareshwar (7a378dcb) — Trimbakeshwar holes treatment, Yash 24 Oct: 45 cards (v1 had 104).
// Flow Omni clips → logo-clean → PD-audio stitch 1920x1080 @24 → NVIDIA 4K. Comp 1280x720 @ 24 fps, scale=3 → 3840x2160.
// Every start/out from overlays/gen_ukhimath_overlays_v2.py (word_timestamps_hindi, no-spoiler by construction).
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";

const BASE_FS = 40, BIG_FS = 56, SMALL_FS = 28;   // Yash 2026-08-04 LAW: 40/56/28.
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -8;

const CTA_TITLE = "हर सर्दी बाबा केदार की डोली पहाड़ से नीचे क्यों उतरती है?";
const CTA_HANDLE = "@DevaKatha";
const CTA_COMMENT_TEXT = "जय बाबा केदार 🙏 छह महीने पहाड़ पर, छह महीने ऊखीमठ में, ये पहली बार इतने विस्तार से जाना।";
// ⛔ https R2, never staticFile() — factory_4k_full pushes only src/, public/ is frozen in the image.
const CTA_SEG = (n: number) =>
  `https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/overlay_assets/ukhimath_cta${n}_seg.mp4`;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

export const UKHIMATH_PHRASES: Phrase[] = [
  { start: 6.19, out: 10.31, lines: [[{ t: "6", c: "y", big: true }, { t: "महीनों", c: "w" }], [{ t: "मंदिर बंद", c: "r" }]] },   // P1
  { start: 30.51, out: 33.91, lines: [[{ t: "60", c: "y", big: true }, { t: "किलोमीटर", c: "w" }, { t: "नीचे", c: "w" }]] },   // P2
  { start: 58.49, out: 61.89, lines: [[{ t: "ऊखीमठ", c: "y" }], [{ t: "ओंकारेश्वर", c: "w" }, { t: "मंदिर", c: "w" }]] },   // P5
  { start: 94.11, out: 101.11, lines: [[{ t: "2013", c: "y", big: true }], [{ t: "जून", c: "w" }, { t: "में", c: "w" }]] },   // P7
  { start: 150.16, out: 153.69, lines: [[{ t: "पंचमुखी", c: "y" }, { t: "भोग", c: "w" }, { t: "मूर्ति", c: "w" }]] },   // P11
  { start: 161.00, out: 164.75, lines: [[{ t: "चल", c: "y" }, { t: "विग्रह", c: "y" }]] },   // P12
  { start: 182.01, out: 185.41, lines: [[{ t: "समाधि", c: "y" }, { t: "पूजा", c: "w" }]] },   // P13
  { start: 210.57, out: 213.97, lines: [[{ t: "8:30", c: "y", big: true }, { t: "कपाट", c: "w" }, { t: "बंद", c: "r" }]] },   // P15
  { start: 231.91, out: 235.31, lines: [[{ t: "पहली", c: "w" }, { t: "रात:", c: "w" }, { t: "रामपुर", c: "y" }]] },   // P17
  { start: 269.44, out: 272.84, lines: [[{ t: "डोली ऊखीमठ पहुँचती", c: "y" }]] },   // P19
  { start: 283.55, out: 287.61, lines: [[{ t: "भोग,", c: "w" }, { t: "अभिषेक,", c: "w" }, { t: "आरती", c: "y" }]] },   // P20
  { start: 325.53, out: 330.27, lines: [[{ t: "रुद्रप्रयाग", c: "y" }], [{ t: "1311", c: "y", big: true }, { t: "मीटर", c: "w" }]] },   // P24
  { start: 350.98, out: 354.38, lines: [[{ t: "नागर", c: "y" }, { t: "शैली", c: "w" }, { t: "का", c: "w" }, { t: "शिखर", c: "w" }]] },   // P26
  { start: 386.04, out: 392.85, lines: [[{ t: "पंच", c: "y" }, { t: "केदार", c: "y" }, { t: "का", c: "w" }, { t: "गद्दीस्थल", c: "w" }]] },   // P29
  { start: 404.28, out: 407.68, lines: [[{ t: "केदारनाथ", c: "y" }, { t: "की", c: "w" }, { t: "गद्दी", c: "w" }, { t: "ऊखीमठ", c: "y" }]] },   // P30
  { start: 427.12, out: 433.15, lines: [[{ t: "1910", c: "y", big: true }, { t: "गजेटियर", c: "w" }]] },   // P32
  { start: 463.47, out: 467.28, lines: [[{ t: "सूर्यवंश", c: "y" }, { t: "के", c: "w" }, { t: "राजा", c: "w" }, { t: "मांधाता", c: "y" }]] },   // P34
  { start: 473.48, out: 476.88, lines: [[{ t: "राजा", c: "w" }, { t: "युवनाश्व", c: "y" }]] },   // P35
  { start: 506.20, out: 509.60, lines: [[{ t: "नवें", c: "w" }, { t: "स्कंध,", c: "w" }, { t: "छठे", c: "w" }, { t: "अध्याय", c: "y" }]] },   // P37
  { start: 529.04, out: 532.44, lines: [[{ t: "मां", c: "y" }, { t: "धाता", c: "y" }]] },   // P39
  { start: 539.68, out: 543.08, lines: [[{ t: "चक्रवर्ती", c: "y" }, { t: "सम्राट", c: "w" }]] },   // P40
  { start: 573.84, out: 577.24, lines: [[{ t: "12", c: "y", big: true }, { t: "बरस", c: "w" }, { t: "एक", c: "w" }, { t: "पैर", c: "y" }]] },   // P43
  { start: 591.14, out: 594.54, lines: [[{ t: "ओम्", c: "y", big: true }, { t: "की", c: "w" }, { t: "ध्वनि", c: "w" }]] },   // P44
  { start: 629.05, out: 632.45, lines: [[{ t: "1000", c: "y", big: true }, { t: "भुजाएँ", c: "w" }]] },   // P47
  { start: 654.02, out: 657.42, lines: [[{ t: "द्वार", c: "w" }, { t: "पर", c: "w" }, { t: "महादेव", c: "y" }]] },   // P48
  { start: 685.38, out: 688.78, lines: [[{ t: "सपने", c: "w" }, { t: "में", c: "w" }, { t: "राजकुमार", c: "y" }]] },   // P51
  { start: 697.87, out: 701.27, lines: [[{ t: "सखी", c: "w" }, { t: "चित्रलेखा", c: "y" }]] },   // P52
  { start: 717.79, out: 721.19, lines: [[{ t: "श्रीकृष्ण", c: "w" }, { t: "के", c: "w" }, { t: "पोते", c: "w" }, { t: "अनिरुद्ध", c: "y" }]] },   // P54
  { start: 749.77, out: 753.17, lines: [[{ t: "नागपाश", c: "y" }, { t: "में", c: "w" }, { t: "क़ैद", c: "w" }]] },   // P56
  { start: 783.33, out: 787.48, lines: [[{ t: "एक", c: "w" }, { t: "ओर", c: "w" }, { t: "श्रीकृष्ण,", c: "y" }], [{ t: "दूसरी", c: "w" }, { t: "ओर", c: "w" }, { t: "महादेव", c: "y" }]] },   // P58
  { start: 797.29, out: 800.69, lines: [[{ t: "सुदर्शन", c: "y" }, { t: "चक्र", c: "w" }]] },   // P59
  { start: 864.45, out: 869.80, lines: [[{ t: "उषामठ", c: "y" }], [{ t: "ऊखीमठ", c: "y", big: true }]] },   // P64
  { start: 938.50, out: 941.90, lines: [[{ t: "रावल", c: "y" }, { t: "का", c: "w" }, { t: "ठिकाना", c: "w" }]] },   // P70
  { start: 992.22, out: 995.66, lines: [[{ t: "कर्नाटक", c: "y" }, { t: "की", c: "w" }, { t: "वीरशैव", c: "w" }, { t: "परंपरा", c: "w" }]] },   // P74
  { start: 1044.91, out: 1048.31, lines: [[{ t: "महाशिवरात्रि", c: "y", big: true }]] },   // P78
  { start: 1141.60, out: 1145.00, lines: [[{ t: "8", c: "y", big: true }, { t: "दिसंबर", c: "w" }, { t: "2024", c: "y" }]] },   // P85
  { start: 1154.48, out: 1157.88, lines: [[{ t: "6,482", c: "y", big: true }, { t: "श्रद्धालु", c: "w" }]] },   // P85
  { start: 1170.25, out: 1173.65, lines: [[{ t: "बादल", c: "y" }, { t: "फटा", c: "r" }]] },   // P86
  { start: 1291.07, out: 1294.47, lines: [[{ t: "भोग", c: "w" }, { t: "मूर्ति", c: "y" }, { t: "सुरक्षित", c: "w" }]] },   // P95
  { start: 1325.62, out: 1329.02, lines: [[{ t: "25", c: "y", big: true }, { t: "जून", c: "w" }]] },   // P99
  { start: 1424.30, out: 1427.70, lines: [[{ t: "86", c: "y", big: true }, { t: "दिन", c: "w" }, { t: "बाद", c: "w" }]] },   // P106
  { start: 1468.87, out: 1472.27, lines: [[{ t: "8", c: "y", big: true }, { t: "बजे", c: "w" }, { t: "कपाट", c: "w" }, { t: "बंद", c: "r" }]] },   // P109
  { start: 1482.38, out: 1485.78, lines: [[{ t: "स्वागत", c: "w" }, { t: "2", c: "y", big: true }, { t: "बार", c: "y" }]] },   // P110
  { start: 1544.53, out: 1547.93, lines: [[{ t: "नर", c: "y" }, { t: "और", c: "w" }, { t: "नारायण", c: "y" }]] },   // P116
  { start: 1636.71, out: 1639.78, lines: [[{ t: "जय", c: "y" }, { t: "बाबा", c: "w" }, { t: "केदार", c: "y" }]] },   // P123
];

const PhraseCard: React.FC<{ p: Phrase }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  let k = 0;
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: p.lineGap ?? LINE_GAP }}>
        {p.lines.map((line, li) => (
          <div key={li} style={{ display: "flex", alignItems: "flex-end", gap: 16, lineHeight: p.lh ?? LH }}>
            {line.map((w, wi) => {
              const delay = k++ * STAGGER;
              const prog = interpolate(t, [delay, delay + DUR], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const fs = w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS;
              return (
                <span key={wi} style={{
                  fontFamily, fontWeight: w.big ? 900 : 800, fontSize: fs, color: col(w.c),
                  textShadow: SH, letterSpacing: p.ls,
                  transform: `translateY(${(1 - prog) * -DROP}px)`, opacity: prog,
                }}>{w.t}</span>
              );
            })}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const Teaser: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const dots = ["", ".", "..", "..."][Math.floor((frame / fps * 1.5) % 4)];
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-end" }}>
      <span style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>
        आगे देखिए{dots}
      </span>
    </AbsoluteFill>
  );
};

const WatchTip: React.FC = () => (
  <AbsoluteFill style={{ padding: PAD, alignItems: "flex-end", justifyContent: "flex-end" }}>
    <div style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>Watch at 1.2x</div>
    <div style={{ fontFamily, fontWeight: 700, fontSize: SMALL_FS * 0.7, color: WHITE, textShadow: SH }}>for best experience</div>
  </AbsoluteFill>
);

// 🔥 The <Sequence from={S(...)} /> durationInFrames={S(...)} values below MUST stay
// NUMERIC LITERALS — build_overlay_windows.py AND cta_segs.py both regex-scrape them.
export const UkhimathOverlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <HolesLayer holes={HOLES_DATA_URI} width={1280} height={720}>
        {UKHIMATH_PHRASES.map((p, i) => (
          <Sequence key={i} from={S(p.start)} durationInFrames={S(p.out - p.start)}>
            <PhraseCard p={p} />
          </Sequence>
        ))}
      </HolesLayer>

      <Sequence from={S(0)} durationInFrames={S(90)}>
        <Teaser />
      </Sequence>
      <Sequence from={S(0)} durationInFrames={S(5)}>
        <WatchTip />
      </Sequence>

      {/* CTA1 — लाइक 309.36 · हाइप 309.77 (कमेंट 311.21 has no tap) */}
      <Sequence from={S(307.70)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(1)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ like: 309.36, hype: 309.77 }, 307.70)} />
      </Sequence>
      {/* CTA2 — सब्सक्राइब 1223.73 (sub-only cluster) */}
      <Sequence from={S(1222.07)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(2)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ sub: 1223.73 }, 1222.07)} />
      </Sequence>
      {/* CTA3 — outro सब्सक्राइब 1618.48 (sub-only cluster) */}
      <Sequence from={S(1616.82)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(3)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ sub: 1618.48 }, 1616.82)} />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
